import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Theme } from '../lib/tokens';
import { FONTS } from '../lib/tokens';
import { Portrait } from '../components/Portrait';
import { Img } from '../components/Img';
import { Icon } from '../components/icons';
import { useAuth } from '../hooks/useAuth';
import { hapticSuccess } from '../lib/haptics';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8080';

interface GraduationSheetProps {
  matchId: string;
  otherUser: { displayName: string; avatarUrl: string | null; photos: string[] };
  t: Theme;
  onClose: () => void;
  onCoupled: () => void;
}

// Confetti rain — ambient celebration
function ConfettiRain({ t, count = 22 }: { t: Theme; count?: number }) {
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 1 }}>
      {Array.from({ length: count }).map((_, i) => {
        const colors = [t.blush, t.terracotta, t.gold, t.sage];
        const left = (i * 53 + 7) % 100;
        const delay = (i * 173) % 1400;
        const dur = 2600 + (i * 211) % 1800;
        const w = 6 + (i % 3) * 3;
        const h = i % 2 ? 11 : 7;
        return (
          <div key={i} style={{
            position: 'absolute', top: -24, left: `${left}%`,
            width: w, height: h,
            borderRadius: i % 3 === 0 ? '100%' : 2,
            background: colors[i % 4],
            ['--cr' as any]: `${360 + (i * 97) % 480}deg`,
            animation: `pmConfetti ${dur}ms linear ${delay}ms infinite`,
          }} />
        );
      })}
    </div>
  );
}

// Photo — real if available, placeholder Portrait otherwise
function UserPhoto({ photo, seed, name, style }: {
  photo: string | null; seed: string; name: string; style: React.CSSProperties;
}) {
  if (photo) {
    return <Img src={photo} alt={name} style={{ objectFit: 'cover', ...style }} />;
  }
  return <Portrait seed={seed} style={style} />;
}

export function GraduationSheet({ matchId, otherUser, t, onClose, onCoupled }: GraduationSheetProps) {
  const { getIdToken, userProfile, refreshProfile } = useAuth();
  const queryClient = useQueryClient();
  const [show, setShow] = useState(false);
  const [step, setStep] = useState<0 | 1 | 2>(0); // 0=offer, 1=waiting, 2=celebrate
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { setTimeout(() => setShow(true), 40); }, []);

  const myPhoto = userProfile?.profile?.photos?.[0] || userProfile?.profile?.avatarUrl || null;
  const theirPhoto = otherUser.photos?.[0] || otherUser.avatarUrl || null;
  const myName = userProfile?.profile?.displayName || 'You';
  const mySeed = userProfile?.id || 'me';
  const theirSeed = otherUser.displayName;

  // Poll for couple formation while in waiting step
  const { data: coupleCheck } = useQuery({
    queryKey: ['couple-me-grad'],
    queryFn: async () => {
      const token = await getIdToken();
      const res = await fetch(`${API_BASE}/api/couples/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.json() as Promise<{ couple: { id: string } | null }>;
    },
    enabled: step === 1,
    refetchInterval: 8_000,
    staleTime: 0,
  });

  useEffect(() => {
    if (step === 1 && coupleCheck?.couple) {
      setStep(2);
    }
  }, [coupleCheck, step]);

  useEffect(() => {
    if (step === 2) hapticSuccess();
  }, [step]);

  async function handleInvite() {
    setLoading(true);
    setError('');
    try {
      const token = await getIdToken();
      const res = await fetch(`${API_BASE}/api/couples/graduate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ matchId }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      if (data.coupled) {
        setStep(2);
      } else {
        setStep(1);
      }
    } catch {
      setError('Something went wrong. Tap to retry.');
    } finally {
      setLoading(false);
    }
  }

  async function handleCoupled() {
    await refreshProfile();
    queryClient.invalidateQueries({ queryKey: ['matches'] });
    queryClient.invalidateQueries({ queryKey: ['couple-me'] });
    onCoupled();
  }

  return (
    <div style={{
      position: 'absolute', inset: 0, zIndex: 300,
      background: 'rgba(31,26,23,0.55)', backdropFilter: 'blur(8px)',
      display: 'flex', flexDirection: 'column',
      opacity: show ? 1 : 0, transition: 'opacity 200ms',
    }} onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          position: 'absolute', inset: 0,
          background: t.paper,
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          transform: show ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 380ms cubic-bezier(0.2,0.8,0.2,1)',
        }}
      >
        {step === 0 && (
          <StepOffer
            t={t}
            myName={myName} myPhoto={myPhoto} mySeed={mySeed}
            theirName={otherUser.displayName} theirPhoto={theirPhoto} theirSeed={theirSeed}
            loading={loading} error={error}
            onInvite={handleInvite}
            onBack={onClose}
          />
        )}
        {step === 1 && (
          <StepWaiting
            t={t}
            myPhoto={myPhoto} mySeed={mySeed}
            theirName={otherUser.displayName} theirPhoto={theirPhoto} theirSeed={theirSeed}
            onCancel={onClose}
          />
        )}
        {step === 2 && (
          <StepCelebration
            t={t}
            myName={myName} myPhoto={myPhoto} mySeed={mySeed}
            theirName={otherUser.displayName} theirPhoto={theirPhoto} theirSeed={theirSeed}
            onDone={handleCoupled}
          />
        )}
      </div>
    </div>
  );
}

// ── Step 0: Make it official? ─────────────────────────────────────────────────

function StepOffer({ t, myName, myPhoto, mySeed, theirName, theirPhoto, theirSeed, loading, error, onInvite, onBack }: {
  t: Theme;
  myName: string; myPhoto: string | null; mySeed: string;
  theirName: string; theirPhoto: string | null; theirSeed: string;
  loading: boolean; error: string;
  onInvite: () => void;
  onBack: () => void;
}) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 110 }} className="scroll-container">
        {/* Nav */}
        <div style={{ padding: '16px 20px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button onClick={onBack} style={{ all: 'unset', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: FONTS.sans, fontSize: 13.5, color: t.inkSoft }}>
            {Icon.back({ s: 16, c: t.inkSoft })} Back
          </button>
        </div>

        {/* Portrait pairing */}
        <div style={{ padding: '32px 24px 18px', display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
          <div className="pm-fade-in pm-d5" style={{ position: 'absolute', width: 230, height: 230, borderRadius: '100%', border: `1px solid ${t.line}`, top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
          <div className="pm-fade-in pm-d6" style={{ position: 'absolute', width: 195, height: 195, borderRadius: '100%', border: `1px dashed ${t.line}`, top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
          <div className="pm-drift-l" style={{ zIndex: 2 }}>
            <UserPhoto photo={myPhoto} seed={mySeed} name={myName}
              style={{ width: 120, height: 152, borderRadius: 20, transform: 'rotate(-5deg) translateX(16px)', boxShadow: t.shadow, display: 'block' }} />
          </div>
          <div className="pm-pop pm-d4" style={{
            zIndex: 10, width: 42, height: 42, borderRadius: '100%',
            background: t.terracotta, display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 6px 20px rgba(200,85,61,0.35)`, flexShrink: 0,
          }}>
            <span className="pm-beat" style={{ display: 'flex' }}>
              {Icon.heart({ s: 18, c: '#fff', fill: '#fff', w: 0 })}
            </span>
          </div>
          <div className="pm-drift-r" style={{ zIndex: 2 }}>
            <UserPhoto photo={theirPhoto} seed={theirSeed} name={theirName}
              style={{ width: 120, height: 152, borderRadius: 20, transform: 'rotate(5deg) translateX(-16px)', boxShadow: t.shadow, display: 'block' }} />
          </div>
        </div>

        {/* Copy */}
        <div className="pm-fade-up pm-d2" style={{ padding: '2px 28px 0' }}>
          <div style={{ fontFamily: FONTS.mono, fontSize: 10.5, letterSpacing: 2, textTransform: 'uppercase', color: t.terracotta, marginBottom: 10 }}>
            You & {theirName.toUpperCase()}
          </div>
          <h2 style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontWeight: 400, fontSize: 40, margin: '0 0 12px', color: t.ink, lineHeight: 0.97, letterSpacing: -0.5 }}>
            Make it<br />official?
          </h2>
          <p style={{ fontFamily: FONTS.sans, fontSize: 14.5, lineHeight: 1.55, color: t.inkSoft, margin: 0 }}>
            A shared profile for the two of you. Browse together, match with other couples, and let Concierge plan outings for more than two.
          </p>
        </div>

        {/* Perks */}
        <div style={{ padding: '22px 24px 16px' }}>
          {[
            { icon: Icon.user,    label: 'One shared profile',       sub: 'Both of you, one card. Either can browse.' },
            { icon: Icon.heart,   label: 'Pair up with other couples', sub: 'Double dates, group dinners, new friends.' },
            { icon: Icon.sparkle, label: 'Concierge plans for four', sub: 'Reservations, activities — the whole thing.' },
          ].map((it, i) => (
            <div key={i} className={`pm-fade-up pm-d${i + 3}`} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', padding: '14px 0', borderBottom: i < 2 ? `1px solid ${t.line}` : 'none' }}>
              <div style={{ width: 38, height: 38, borderRadius: '100%', background: t.paperAlt, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {it.icon({ s: 16, c: t.terracotta })}
              </div>
              <div>
                <div style={{ fontFamily: FONTS.sans, fontSize: 14, fontWeight: 600, color: t.ink }}>{it.label}</div>
                <div style={{ fontFamily: FONTS.sans, fontSize: 12.5, color: t.inkMute, marginTop: 2 }}>{it.sub}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Couple preview */}
        <div className="pm-fade-up pm-d6" style={{ padding: '4px 20px 24px' }}>
          <div style={{ fontFamily: FONTS.sans, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 1.5, color: t.inkMute, marginBottom: 10 }}>Preview of your couple profile</div>
          <div style={{ borderRadius: 20, overflow: 'hidden', background: t.card, border: `1px solid ${t.line}`, boxShadow: t.shadow }}>
            <div style={{ display: 'flex', height: 130, position: 'relative' }}>
              <UserPhoto photo={myPhoto} seed={mySeed} name={myName} style={{ width: '50%', height: '100%', display: 'block' }} />
              <div style={{ width: 1, background: 'rgba(255,255,255,0.3)', zIndex: 5, flexShrink: 0 }} />
              <UserPhoto photo={theirPhoto} seed={theirSeed} name={theirName} style={{ width: '50%', height: '100%', display: 'block' }} />
              <div style={{ position: 'absolute', top: 8, left: 8, width: 28, height: 28, borderRadius: '100%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', zIndex: 6 }}>
                {Icon.heart({ s: 13, c: t.terracotta, fill: t.terracotta, w: 0 })}
              </div>
            </div>
            <div style={{ padding: '12px 14px 14px' }}>
              <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 22, color: t.ink, letterSpacing: -0.3 }}>
                {myName} & {theirName}
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 9px', borderRadius: 100, background: `${t.sage}22`, border: `1px solid ${t.sage}55`, fontFamily: FONTS.sans, fontSize: 10.5, fontWeight: 600, color: t.sage }}>✦ couple</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div style={{ padding: '14px 24px 32px', background: `linear-gradient(to top, ${t.paper} 65%, transparent)`, position: 'absolute', bottom: 0, left: 0, right: 0 }}>
        {error && <div style={{ fontFamily: FONTS.sans, fontSize: 12, color: t.terracotta, textAlign: 'center', marginBottom: 8 }}>{error}</div>}
        <button
          onClick={onInvite}
          disabled={loading}
          className="pm-press"
          style={{
            all: 'unset', cursor: loading ? 'default' : 'pointer',
            width: '100%', boxSizing: 'border-box',
            padding: '15px 22px', borderRadius: 100, textAlign: 'center',
            background: t.terracotta, color: '#FBF6EC',
            fontFamily: FONTS.sans, fontSize: 15, fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            opacity: loading ? 0.7 : 1,
          }}
        >
          {loading
            ? <><div style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid #FBF6EC', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} /> Sending invite…</>
            : <>{Icon.heart({ s: 16, c: '#FBF6EC', fill: '#FBF6EC', w: 0 })} Invite {theirName}</>
          }
        </button>
        <div style={{ textAlign: 'center', marginTop: 10, fontFamily: FONTS.sans, fontSize: 11.5, color: t.inkMute }}>
          {theirName} will get a notification on their end.
        </div>
      </div>
    </div>
  );
}

// ── Step 1: Waiting ────────────────────────────────────────────────────────────

function StepWaiting({ t, myPhoto, mySeed, theirName, theirPhoto, theirSeed, onCancel }: {
  t: Theme;
  myPhoto: string | null; mySeed: string;
  theirName: string; theirPhoto: string | null; theirSeed: string;
  onCancel: () => void;
}) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 110 }} className="scroll-container">
        {/* Portrait + pulsing ring */}
        <div style={{ padding: '50px 24px 20px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 20, position: 'relative' }}>
          <div className="pm-drift-l">
            <UserPhoto photo={myPhoto} seed={mySeed} name="You"
              style={{ width: 106, height: 134, borderRadius: 18, opacity: 0.75, boxShadow: t.shadow, display: 'block' }} />
          </div>
          <div className="pm-pop pm-d2 pm-float" style={{ width: 40, height: 40, borderRadius: '100%', background: t.terracotta, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 0 0 8px ${t.blush}40, 0 0 0 16px ${t.blush}20`, flexShrink: 0 }}>
            {Icon.heart({ s: 17, c: '#fff', fill: '#fff', w: 0 })}
          </div>
          {/* Partner with pulsing border */}
          <div className="pm-drift-r" style={{ position: 'relative' }}>
            <div style={{ position: 'absolute', inset: -6, borderRadius: 24, border: `2px solid ${t.blush}`, animation: 'couplePulse 1.8s ease-in-out infinite' }} />
            <UserPhoto photo={theirPhoto} seed={theirSeed} name={theirName}
              style={{ width: 106, height: 134, borderRadius: 18, boxShadow: t.shadow, position: 'relative', zIndex: 1, display: 'block' }} />
          </div>
        </div>

        {/* Status */}
        <div className="pm-fade-up pm-d2" style={{ padding: '4px 28px 0' }}>
          <div style={{ fontFamily: FONTS.mono, fontSize: 10.5, letterSpacing: 2, textTransform: 'uppercase', color: t.sage, marginBottom: 10 }}>
            ✓ Invite sent
          </div>
          <h2 style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontWeight: 400, fontSize: 38, margin: '0 0 10px', color: t.ink, lineHeight: 1, letterSpacing: -0.4 }}>
            Waiting for<br />{theirName}…
          </h2>
          <p style={{ fontFamily: FONTS.sans, fontSize: 14, lineHeight: 1.55, color: t.inkSoft, margin: 0 }}>
            This can take anywhere from immediately to whenever they look up from their phone.
          </p>
        </div>

        {/* What they see preview */}
        <div className="pm-fade-up pm-d4" style={{ padding: '28px 20px 0' }}>
          <div style={{ fontFamily: FONTS.sans, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 1.5, color: t.inkMute, marginBottom: 10 }}>
            What {theirName} sees on their end
          </div>
          <div style={{ borderRadius: 22, padding: '18px', background: t.card, border: `1px solid ${t.line}`, boxShadow: t.shadow }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 14 }}>
              <UserPhoto photo={myPhoto} seed={mySeed} name="You" style={{ width: 42, height: 42, borderRadius: '100%', display: 'block', flexShrink: 0 }} />
              <div>
                <div style={{ fontFamily: FONTS.sans, fontSize: 11, color: t.inkMute }}>from preminade</div>
                <div style={{ fontFamily: FONTS.sans, fontSize: 14, fontWeight: 600, color: t.ink }}>
                  wants to go couples with you
                </div>
              </div>
            </div>
            <p style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 18, color: t.ink, margin: '0 0 16px', lineHeight: 1.35 }}>
              "I think we should make it official on here. Browse together, double dates, the whole thing."
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1, padding: '12px', borderRadius: 12, background: t.ink, color: t.paper, fontFamily: FONTS.sans, fontSize: 13, fontWeight: 600, textAlign: 'center' }}>Yes, let's</div>
              <div style={{ flex: 1, padding: '12px', borderRadius: 12, background: t.paperAlt, color: t.inkSoft, fontFamily: FONTS.sans, fontSize: 13, fontWeight: 500, textAlign: 'center' }}>Maybe later</div>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div style={{ padding: '14px 24px 32px', background: `linear-gradient(to top, ${t.paper} 70%, transparent)`, position: 'absolute', bottom: 0, left: 0, right: 0 }}>
        <button
          onClick={onCancel}
          className="pm-press"
          style={{
            all: 'unset', cursor: 'pointer', width: '100%', boxSizing: 'border-box',
            padding: '14px 22px', borderRadius: 100, textAlign: 'center',
            background: 'transparent', border: `1px solid ${t.line}`,
            fontFamily: FONTS.sans, fontSize: 15, fontWeight: 600, color: t.ink,
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Step 2: Celebration ────────────────────────────────────────────────────────

function StepCelebration({ t, myName, myPhoto, mySeed, theirName, theirPhoto, theirSeed, onDone }: {
  t: Theme;
  myName: string; myPhoto: string | null; mySeed: string;
  theirName: string; theirPhoto: string | null; theirSeed: string;
  onDone: () => void;
}) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 110 }} className="scroll-container">

        {/* Hero with confetti + portraits */}
        <div style={{ position: 'relative', overflow: 'hidden', minHeight: 300 }}>
          <div style={{ position: 'absolute', top: -80, left: -80, width: 280, height: 280, borderRadius: '100%', background: `radial-gradient(circle, ${t.blush} 0%, transparent 70%)`, opacity: 0.55, pointerEvents: 'none', animation: 'pmBreathe 3.4s ease-in-out infinite' }} />
          <div style={{ position: 'absolute', top: -40, right: -60, width: 200, height: 200, borderRadius: '100%', background: `radial-gradient(circle, ${t.gold} 0%, transparent 70%)`, opacity: 0.4, pointerEvents: 'none', animation: 'pmBreathe 3.4s ease-in-out 0.8s infinite' }} />
          <ConfettiRain t={t} count={22} />

          <div style={{ paddingTop: 52, display: 'flex', justifyContent: 'center', alignItems: 'flex-end', position: 'relative', gap: 0 }}>
            <div className="pm-drift-l" style={{ zIndex: 2 }}>
              <UserPhoto photo={myPhoto} seed={mySeed} name={myName}
                style={{ width: 136, height: 172, borderRadius: 24, transform: 'rotate(-6deg) translateX(22px)', boxShadow: '0 24px 55px rgba(60,30,15,0.22)', display: 'block' }} />
            </div>
            <div className="pm-pop pm-d4" style={{
              position: 'absolute', bottom: 46, left: '50%', transform: 'translateX(-50%)',
              zIndex: 10, width: 48, height: 48, borderRadius: '100%',
              background: t.terracotta, display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: `0 8px 26px rgba(200,85,61,0.45)`,
            }}>
              <span className="pm-beat" style={{ display: 'flex' }}>
                {Icon.heart({ s: 22, c: '#fff', fill: '#fff', w: 0 })}
              </span>
            </div>
            <div className="pm-drift-r" style={{ zIndex: 2 }}>
              <UserPhoto photo={theirPhoto} seed={theirSeed} name={theirName}
                style={{ width: 136, height: 172, borderRadius: 24, transform: 'rotate(6deg) translateX(-22px)', boxShadow: '0 24px 55px rgba(60,30,15,0.22)', display: 'block' }} />
            </div>
          </div>
        </div>

        {/* Copy */}
        <div className="pm-fade-up pm-d3" style={{ padding: '20px 28px 0' }}>
          <div style={{ fontFamily: FONTS.mono, fontSize: 10.5, letterSpacing: 2, textTransform: 'uppercase', color: t.terracotta, marginBottom: 10 }}>✦ It's official</div>
          <h2 style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontWeight: 400, fontSize: 44, margin: '0 0 10px', color: t.ink, lineHeight: 0.95, letterSpacing: -0.5 }}>
            {theirName}<br />said yes.
          </h2>
          <p style={{ fontFamily: FONTS.sans, fontSize: 14.5, lineHeight: 1.55, color: t.inkSoft, margin: 0 }}>
            You're a couple on preminade. Your shared profile is live. Either of you can browse and plan from now on.
          </p>
        </div>

        {/* Couple profile preview */}
        <div className="pm-fade-up pm-d5" style={{ padding: '20px 20px 4px' }}>
          <div style={{ fontFamily: FONTS.sans, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 1.5, color: t.inkMute, marginBottom: 10 }}>Your couple profile</div>
          <div style={{ borderRadius: 22, overflow: 'hidden', background: t.card, border: `1px solid ${t.line}`, boxShadow: t.shadow }}>
            <div style={{ display: 'flex', height: 148, position: 'relative' }}>
              <UserPhoto photo={myPhoto} seed={mySeed} name={myName} style={{ width: '50%', height: '100%', display: 'block' }} />
              <div style={{ width: 1, background: 'rgba(255,255,255,0.3)', flexShrink: 0 }} />
              <UserPhoto photo={theirPhoto} seed={theirSeed} name={theirName} style={{ width: '50%', height: '100%', display: 'block' }} />
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 34, height: 34, borderRadius: '100%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 14px rgba(0,0,0,0.14)', zIndex: 5 }}>
                {Icon.heart({ s: 14, c: t.terracotta, fill: t.terracotta, w: 0 })}
              </div>
            </div>
            <div style={{ padding: '14px 16px 16px' }}>
              <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 24, color: t.ink, letterSpacing: -0.3 }}>
                {myName} & {theirName}
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 9px', borderRadius: 100, background: `${t.sage}22`, border: `1px solid ${t.sage}55`, fontFamily: FONTS.sans, fontSize: 11, fontWeight: 600, color: t.sage }}>✦ met on preminade</span>
              </div>
            </div>
          </div>
        </div>

        {/* Concierge congratulations card */}
        <div className="pm-fade-up pm-d6" style={{ padding: '16px 20px 28px' }}>
          <div style={{ borderRadius: 20, padding: '18px 20px', background: t.ink, color: t.paper, position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: -40, right: -30, width: 140, height: 140, borderRadius: '100%', background: `radial-gradient(circle, ${t.blush} 0%, transparent 65%)`, opacity: 0.5 }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, position: 'relative' }}>
              {Icon.sparkle({ s: 14, c: t.blush })}
              <span style={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase', color: t.blush }}>Concierge</span>
            </div>
            <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 19, lineHeight: 1.3, position: 'relative' }}>
              "Congratulations. I've already drafted your first couples outing — dinner for four at Rochelle, Thursday? I need a 'yes' from both of you."
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div style={{ padding: '14px 24px 32px', background: `linear-gradient(to top, ${t.paper} 70%, transparent)`, position: 'absolute', bottom: 0, left: 0, right: 0, display: 'flex', gap: 10 }}>
        <button
          onClick={onDone}
          className="pm-press"
          style={{
            all: 'unset', cursor: 'pointer', flex: 1.4, boxSizing: 'border-box',
            padding: '15px 22px', borderRadius: 100, textAlign: 'center',
            background: t.terracotta, color: '#FBF6EC',
            fontFamily: FONTS.sans, fontSize: 15, fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
        >
          {Icon.flame({ s: 16, c: '#FBF6EC' })} Browse as a couple
        </button>
      </div>
    </div>
  );
}
